import { OrderHistoryReceipt } from '@tovala/browser-apis-combinedapi'
import { uniqBy } from 'lodash-es'

import { addToDate } from 'utils/dates'

import { getReceiptMealsByID, RatingPromptToShow } from './utils'

const PREVIEW_RATING_PROMPT_PARAM = 'previewRatingPrompt'
const PREVIEW_RATING_BANNER_PARAM = 'previewRatingBanner'

export type RatingPromptPreview =
  | { type: 'meals'; mealIDs: number[] }
  | { type: 'multi' }
  | { type: 'onboarding' }
  | { type: 'single' }

export function getRatingPromptPreview({
  search,
}: {
  search: string
}): RatingPromptPreview | null {
  if (isProductionBuild()) {
    return null
  }

  const params = new URLSearchParams(search)
  if (!params.has(PREVIEW_RATING_PROMPT_PARAM)) {
    return null
  }

  const previewValue = params.get(PREVIEW_RATING_PROMPT_PARAM) ?? ''
  if (previewValue === 'multi' || previewValue === 'onboarding') {
    return { type: previewValue }
  }

  const mealIDs = previewValue
    .split(',')
    .map((mealID) => Number.parseInt(mealID, 10))
    .filter((mealID) => !Number.isNaN(mealID))

  return mealIDs.length > 0 ? { type: 'meals', mealIDs } : { type: 'single' }
}

export function getRatingPromptPreviewToShow({
  now,
  orderHistoryReceipts,
  preview,
  reviewedMealIDs,
}: {
  now: Date
  orderHistoryReceipts: OrderHistoryReceipt[]
  preview: RatingPromptPreview
  reviewedMealIDs: number[]
}): RatingPromptToShow | null {
  if (isProductionBuild()) {
    return null
  }

  const promptToShow = getRatingPromptPreviewWithoutCookDays({
    orderHistoryReceipts,
    preview,
    reviewedMealIDs,
  })

  if (promptToShow?.type !== 'multi') {
    return promptToShow
  }

  // Dev environments rarely have oven cook history, so previews make up a
  // cook day per meal to show the "Cooked ..." labels.
  return {
    type: 'multi',
    mealsToRate: promptToShow.mealsToRate.map(({ meal }, daysAgo) => ({
      cookedAt: addToDate(now, { quantity: -daysAgo, units: 'days' }),
      meal,
    })),
  }
}

function getRatingPromptPreviewWithoutCookDays({
  orderHistoryReceipts,
  preview,
  reviewedMealIDs,
}: {
  orderHistoryReceipts: OrderHistoryReceipt[]
  preview: RatingPromptPreview
  reviewedMealIDs: number[]
}): RatingPromptToShow | null {
  if (preview.type === 'meals') {
    return getRequestedMealsPromptPreview({
      mealIDs: preview.mealIDs,
      orderHistoryReceipts,
    })
  }

  if (preview.type === 'multi') {
    return getMultiMealPromptPreview({ orderHistoryReceipts, reviewedMealIDs })
  }

  return getSingleMealPromptPreview({ orderHistoryReceipts, reviewedMealIDs })
}

function getRequestedMealsPromptPreview({
  mealIDs,
  orderHistoryReceipts,
}: {
  mealIDs: number[]
  orderHistoryReceipts: OrderHistoryReceipt[]
}): RatingPromptToShow | null {
  const receiptMealsByID = getReceiptMealsByID(orderHistoryReceipts)
  const mealsToRate = mealIDs.flatMap((mealID) => {
    const meal = receiptMealsByID.get(mealID)
    return meal ? [{ cookedAt: null, meal }] : []
  })

  if (mealsToRate.length > 1) {
    return { type: 'multi', mealsToRate }
  }

  return mealsToRate[0] ? { type: 'single', mealToRate: mealsToRate[0] } : null
}

function getMultiMealPromptPreview({
  orderHistoryReceipts,
  reviewedMealIDs,
}: {
  orderHistoryReceipts: OrderHistoryReceipt[]
  reviewedMealIDs: number[]
}): RatingPromptToShow | null {
  const reviewedMealIDsSet = new Set(reviewedMealIDs)

  for (const receipt of orderHistoryReceipts) {
    const receiptMeals = uniqBy(receipt.selectedMeals ?? [], 'id')
    if (receiptMeals.length < 2) {
      continue
    }

    const unratedReceiptMeals = receiptMeals.filter(
      (meal) => !reviewedMealIDsSet.has(meal.id),
    )
    // Test accounts often have every past meal rated already, so fall back to
    // the whole order rather than showing nothing.
    const mealsToShow =
      unratedReceiptMeals.length > 1 ? unratedReceiptMeals : receiptMeals

    return {
      type: 'multi',
      mealsToRate: mealsToShow.map((meal) => ({ cookedAt: null, meal })),
    }
  }

  return null
}

function getSingleMealPromptPreview({
  orderHistoryReceipts,
  reviewedMealIDs,
}: {
  orderHistoryReceipts: OrderHistoryReceipt[]
  reviewedMealIDs: number[]
}): RatingPromptToShow | null {
  const reviewedMealIDsSet = new Set(reviewedMealIDs)
  const receiptMeals = Array.from(
    getReceiptMealsByID(orderHistoryReceipts).values(),
  )
  const meal =
    receiptMeals.find((meal) => !reviewedMealIDsSet.has(meal.id)) ??
    receiptMeals[0]

  return meal ? { type: 'single', mealToRate: { cookedAt: null, meal } } : null
}

export function isRatingBannerPreviewed({ search }: { search: string }) {
  return (
    !isProductionBuild() &&
    new URLSearchParams(search).has(PREVIEW_RATING_BANNER_PARAM)
  )
}

// Reads the env directly rather than through getEnvVar so the comparison is a
// build-time constant and production builds drop the preview code.
function isProductionBuild() {
  return import.meta.env.VITE_APP_ENV === 'production'
}
